// noinspection JSUnusedGlobalSymbols,JSUnresolvedReference

import React from 'react';
import {
  Action as RcmAction,
  cognito as rcmCognitoReducer,
  CognitoState as RcmState,
  Login as RcmLogin,
  NewPasswordRequired as RcmNewPasswordRequired,
  PasswordReset as RcmPasswordReset,
  refresh as rcmRefresh,
  registerUser as rcmRegisterUser,
  setupCognito as rcmSetupCognito,
  updateAttributes as rcmUpdateAttributes,
} from 'react-cognito-mm';
import {
  CognitoIdToken as AciCognitoIdToken,
  CognitoRefreshToken as AciCognitoRefreshToken,
  CognitoUser as AciCognitoUser,
} from 'amazon-cognito-identity-js';
import hoistNonReactStatics from 'hoist-non-react-statics';

import locationReplace from 'utils/dom/location/locationReplace.util';
import delayed from 'utils/functions/delayed.util';
import logger from 'utils/logger.util';

// NOTE: renaming them here to avoid name clashes with the originals
// NOTE: the entire project should use our own renamed versions
// NOTE: the react-cognito-mm and amazon-cognito-identity-js libraries should only be imported in this file
export { AciCognitoIdToken, rcmCognitoReducer, rcmSetupCognito };

// NOTE: ui.booking may not have a logger that handles an array of tags here
const L = logger('COGNITO'); // eslint-disable-line no-unused-vars

// NOTE: ui.booking has just a mock placeholder since this project doesn't have UMSG
const UMSG = Object.freeze({
  add: options => void L.warn('UMSG.add()', options?.message, options?.extra),
  LEVEL: {},
});

const IS_CYPRESS = false;
const USE_SPAMMY_EXTEND_LOG = false;

const SECOND = 1000;
const MINUTE = 60 * SECOND;
const HOUR = 60 * MINUTE;

const NO_OP_FN = () => {};

export const COGNITO_STATE = Object.freeze({
  // SEE: https://github.com/isotoma/react-cognito/blob/master/manual/states.png
  // SEE: node_modules/react-cognito-mm

  // settled states:

  authenticated: RcmState.AUTHENTICATED, // 'AUTHENTICATED',
  in: RcmState.LOGGED_IN, // 'LOGGED_IN',
  out: RcmState.LOGGED_OUT, // 'LOGGED_OUT',

  // "is still processing" state:

  loading: RcmState.LOGGING_IN, // 'LOGGING_IN',

  // in a state of some requirement:

  needConfirmation: RcmState.CONFIRMATION_REQUIRED, // 'CONFIRMATION_REQUIRED'
  needEmailVerify: RcmState.EMAIL_VERIFICATION_REQUIRED, // 'EMAIL_VERIFICATION_REQUIRED',
  needMultiFactorAuth: RcmState.MFA_REQUIRED, // 'MFA_REQUIRED',
  needNewPassword: RcmState.NEW_PASSWORD_REQUIRED, // 'NEW_PASSWORD_REQUIRED',
});

export const COGNITO_ATTRIBUTE = Object.freeze({
  onboardingCompleted: 'custom:onboarding_completed',
  salonsManage: 'custom:salons_managed',
  salonsAccess: 'custom:salons_with_access',
});

export const rcmClearCacheAction = RcmAction.clearCache;
export const AT_COGNITO_LOGOUT = RcmAction.logout()?.type;
export const AT_COGNITO_ERROR = RcmAction.error()?.type;
export const AT_LOGOUT_SUCCESS = 'LOGOUT_SUCCESS';
export const AT_COGNITO_UPDATE_USER_ATTRIBUTES_VERIFIED_EMAIL = 'COGNITO_UPDATE_USER_ATTRIBUTES_VERIFIED_EMAIL';

export const selectCognito = $ => $?.cognito ?? null;
export const selectCognitoAttributes = $ => $?.cognito?.attributes ?? null;
export const selectManageIds = $ => $?.cognito?.attributes?.[COGNITO_ATTRIBUTE.salonsManage] ?? null;
export const selectAccessIds = $ => $?.cognito?.attributes?.[COGNITO_ATTRIBUTE.salonsAccess] ?? null;
export const selectCognitoAttributeName = $ => $?.cognito?.attributes?.name ?? {};
export const selectCognitoAttributePhone = $ => $?.cognito?.attributes?.phone_number ?? {};
export const selectCognitoAttributeSub = $ => $?.cognito.attributes.sub ?? null;
export const selectCognitoError = $ => $?.cognito?.error ?? null;
export const selectCognitoState = $ => $?.cognito.state ?? null;
export const selectCognitoUser = $ => $?.cognito?.user ?? null;
export const selectCognitoUserObject = $ => $?.cognito?.user ?? {};
export const selectCognitoUserName = $ => $?.cognito.user.username;
export const selectCognitoUserIdPayload = $ => $?.cognito?.user?.signInUserSession?.idToken?.payload;

export const selectMainToken = state => {
  const idToken = state?.cognito?.user?.signInUserSession?.idToken;

  if (!idToken) return '';
  if ('function' !== typeof idToken.getJwtToken) return '';

  return idToken.getJwtToken();
};

export const fromStoreToToken = store =>
  'function' === typeof store?.getState ? selectMainToken(store.getState()) : '';

const fromStateToMainToken = state => {
  const idToken = state?.cognito?.user?.signInUserSession?.idToken;

  if (!idToken) return '';
  if ('function' !== typeof idToken.getJwtToken) return '';

  return idToken.getJwtToken();
};

export const createCognitoId = options => {
  const anyId = options?.idToken ?? options?.IdToken;
  const strId = null === anyId || void 1 === anyId ? '' : String(anyId);

  if (!strId) L.warn('createCognitoId()', 'Bad idToken/IdToken in options:', options);

  return new AciCognitoIdToken({ IdToken: strId });
};

export const feCognitoError = options => {
  const error = options?.error;
  const err = options?.err;
  const e = options?.e;

  const data = { error, err, e };
  const single = error ?? err ?? e ?? options;

  return { type: AT_COGNITO_ERROR, error: single, data };
};

export const feCognitoSessionRefreshed = options => {
  const user = options?.user;
  const session = options?.session;

  const data = { session, user };

  return { type: 'COGNITO_REFRESH', user, data };
};

const cognitoRefreshUserSession = options => {
  const FTAG = 'cognitoRefreshUserSession';

  try {
    const Pool = options?.userPool;
    const RefreshToken = options?.refreshToken;
    const Username = options?.userName;
    const dispatch = 'function' === typeof options?.dispatch ? options?.dispatch : NO_OP_FN;

    return new Promise((resolve, reject) => {
      const cognitoRefreshToken = new AciCognitoRefreshToken({ RefreshToken });
      const user = new AciCognitoUser({ Username, Pool });

      const callback = (error, session) => {
        if (error) {
          L.warn(FTAG, 'callback()', 'options:', options, 'error:', error);
          dispatch(feCognitoError({ error }));
          reject(error);
        } else {
          dispatch(feCognitoSessionRefreshed({ user, session }));
          resolve(session);
        }
      };

      user.refreshSession(cognitoRefreshToken, callback);
    });
  } catch (e) {
    L.warn(FTAG, 'Using options:', options, 'got an error:', e);
  }
};

export const cognitoForgotPassword = options => {
  const FTAG = 'cognitoForgotPassword';

  try {
    const Pool = options?.userPool;
    const Username = options?.userName;
    const dispatch = 'function' === typeof options?.dispatch ? options?.dispatch : NO_OP_FN;

    return new Promise((resolve, reject) => {
      const user = new AciCognitoUser({ Username, Pool });

      user.forgotPassword({
        onSuccess: result => {
          dispatch(RcmAction.continuePasswordResetFlow(user));
          resolve(result);
        },
        onFailure: error => {
          dispatch(RcmAction.beginPasswordResetFlow(user, error.message));
          reject(error);
        },
      });
    });
  } catch (e) {
    L.warn(FTAG, 'Using options:', options, 'got an error:', e);
  }
};

const fromCognitoSessionToIdExpiration = session =>
  'function' === typeof session?.idToken?.getExpiration ? session.idToken.getExpiration() : void 1;

const fromCognitoSessionToIdIssuedAt = session =>
  'function' === typeof session?.idToken?.getIssuedAt ? session.idToken.getIssuedAt() : void 1;

const fromCognitoSessionToRefreshToken = session =>
  'function' === typeof session?.refreshToken?.getToken ? session.refreshToken.getToken() : void 1;

const fromCognitoSessionToIdToken = session =>
  'function' === typeof session?.idToken?.getJwtToken ? session.idToken.getJwtToken() : void 1;

const isSessionNearExpiration = session => {
  const expirationInSeconds = fromCognitoSessionToIdExpiration(session);
  const issuedInSeconds = fromCognitoSessionToIdIssuedAt(session);

  // NOTE: both expiration and issued are in seconds, but time in JS is in milliseconds

  const lifespan = (expirationInSeconds - issuedInSeconds) * SECOND;

  // one minute offset for shorter lifespan than an hour
  // 10 minutes offset for a lifespan shorter than half a day
  // one hour offset for a lifespan longer than half a day
  const offsetInMillis = 12 * HOUR < lifespan ? HOUR : HOUR < lifespan ? 10 * MINUTE : MINUTE;
  const limitInMillis = Date.now() + offsetInMillis;

  return expirationInSeconds * SECOND < limitInMillis;
};

const refreshToken = async options => {
  const FTAG = 'refreshToken()';

  // using let here, so they can be modified in the try block and accessed in the catch block
  let isRefreshNeeded = false;
  let isTokenUpdated = false;

  try {
    const store = options?.store ?? {};
    try {
      const nextOrDispatch = options?.next ?? store?.dispatch;
      const dispatch = 'function' === typeof nextOrDispatch ? nextOrDispatch : NO_OP_FN;
      const force = !!options?.force;

      const oldState = options?.state ?? ('function' === typeof store?.getState ? store?.getState() : void 1);

      const userPool = oldState?.cognito?.userPool ?? null;
      const userName = oldState?.cognito?.user?.username ?? null;
      const oldSession = oldState?.cognito?.user?.signInUserSession ?? null;

      const oldMainToken = fromCognitoSessionToIdToken(oldSession);
      const refreshToken = fromCognitoSessionToRefreshToken(oldSession);

      isRefreshNeeded = force || isSessionNearExpiration(oldSession);

      const session = isRefreshNeeded
        ? await cognitoRefreshUserSession({ userPool, userName, refreshToken, dispatch })
        : void 1;

      const newMainToken = 'function' === typeof store?.getState ? fromStateToMainToken(store.getState()) : '';

      // it should be the same session you get as from calling fromStoreToToken(store) having the new token
      isTokenUpdated = oldMainToken !== newMainToken;

      if (isRefreshNeeded && !isTokenUpdated) {
        L.warn(FTAG, 'Needed:', isRefreshNeeded, 'but updated:', isTokenUpdated, session, oldMainToken, newMainToken);
      }

      return { isRefreshNeeded, isTokenUpdated, mainToken: newMainToken, session, oldMainToken, newMainToken };
    } catch (error) {
      L.warn(FTAG, 'inner-catch', error);

      return IS_CYPRESS
        ? { error, isCypressError: true, mainToken: 'CypressTestToken' }
        : { error, isRefreshError: true, isRefreshNeeded, isTokenUpdated };
    }
  } catch (error) {
    L.warn(FTAG, 'outer-catch', error);
    return { error, isImplementError: true, isRefreshNeeded, isTokenUpdated };
  }
};

const ERROR_MESSAGE_IMPLEMENT = Object.freeze(
  [
    //
    'There was a problem extending your working session automatically.',
    'Please try refreshing your page.',
  ].join(' '),
);

const ERROR_MESSAGE_REFRESH = Object.freeze(
  [
    //
    'Unable to extend your working session automatically.',
    'Please refresh your page',
  ].join(' '),
);

let scheduled = void 1;
export const scheduleExtendSession = options => {
  const FTAG = 'scheduleExtendSession()';

  const info = {};
  const o = { ...(options ?? void 1), force: true };
  try {
    if (scheduled) {
      if (USE_SPAMMY_EXTEND_LOG) {
        L.debug(FTAG, 'Not scheduling session extend w/ options:', o, 'because scheduled:', scheduled);
      }
      return false;
    }

    const state = o?.store?.getState?.();
    info.user = selectCognitoUser(state);
    if (USE_SPAMMY_EXTEND_LOG) {
      info.cognito = selectCognito(state);
      L.debug(FTAG, 'info:', info);
    }

    if (!info.user) {
      if (USE_SPAMMY_EXTEND_LOG) {
        L.debug(FTAG, 'Not scheduling session extend w/ options:', o, 'because user:', info.user);
      }
      return false;
    }
  } catch (e) {
    L.debug(FTAG, 'Not scheduling session extend w/ options:', o, 'because:', e);
  }

  const callback = () => {
    const state = o?.store?.getState?.();
    info.cognito = selectCognito(state);
    info.user = selectCognitoUser(state);
    L.debug(FTAG, 'callback()', 'info:', info);

    extendSession(o)
      .then(r => L.debug(FTAG, 'Pass scheduled:', scheduled, 'w/ options:', o, '->', r))
      .catch(e => L.warn(FTAG, 'Fail scheduled:', scheduled, 'w/ options:', o, '->', e))
      .finally(() => {
        scheduled = null;
      });
  };

  const delay = Number.isSafeInteger(options?.delay) && options?.delay > 0 ? options?.delay : 1000 * 60 * 10;
  scheduled = delayed(delay, callback);
  L.debug(FTAG, 'Session extend w/ options:', o, 'scheduled:', scheduled);

  return scheduled;
};

export const extendSession = async options => {
  const refreshTokenResult = await refreshToken(options);
  const error = refreshTokenResult?.error;

  if (!error) {
    L.debug('()', 'refreshTokenResult:', refreshTokenResult);

    return refreshTokenResult;
  }

  L.warn('()', error, 'refreshTokenResult:', refreshTokenResult);

  if (refreshTokenResult?.isImplementError) {
    UMSG.add({ unique: true, level: UMSG.LEVEL.warning, message: ERROR_MESSAGE_IMPLEMENT, extra: options });
  } else if (refreshTokenResult?.isRefreshError) {
    UMSG.add({ unique: true, level: UMSG.LEVEL.warning, message: ERROR_MESSAGE_REFRESH, extra: options });
  } else {
    UMSG.add({ unique: true, level: UMSG.LEVEL.warning, message: error?.message ?? error, extra: options });
  }

  return refreshTokenResult;
};

const xeCognitoLogout = () => ({ type: AT_COGNITO_LOGOUT });
const xeLogoutSuccess = () => ({ type: AT_LOGOUT_SUCCESS, isFetching: false, isAuthenticated: false });

export const xeRegisterUser = options => async dispatch => {
  // SEE: https://www.npmjs.com/package/amazon-cognito-identity-js
  // SEE: https://github.com/aws-amplify/amplify-js

  const { userPool, config, username, password, attributes } = options ?? {};
  try {
    const data = await rcmRegisterUser(userPool, config, username, password, attributes, dispatch);
    L.debug('()', data);
    return data;
  } catch (error) {
    // use debug here, so you don't print extra info due to warn being executed in production
    L.debug('()', { username, attributes, config, userPool });
    L.warn('()', error);
    throw error;
  }
};

export const refreshCognitoUserToken = async options => {
  const FTAG = 'refreshCognitoUserToken()';

  const { state, dispatch } = options ?? {};
  let userPool = void 1;
  let userName = void 1;
  let refreshToken = void 1;

  try {
    userPool = options?.userPool ?? state?.cognito?.userPool;
    userName = options?.userName ?? state?.cognito?.user?.username;
    refreshToken = options?.refreshToken ?? state?.cognito?.user?.signInUserSession?.refreshToken?.getToken?.();

    const result = await rcmRefresh(userPool, userName, refreshToken, dispatch);
    L.debug(FTAG, 'result:', result);

    return result;
  } catch (e) {
    L.warn(FTAG, { userPool, userName, refreshToken, dispatch }, e);
    throw e;
  }
};

export const updateUserAttributes = async options => {
  const FTAG = 'updateUserAttributes()';
  try {
    const cognitoUser = options?.cognitoUser;
    const attributes = options?.attributes;

    const result = await rcmUpdateAttributes(cognitoUser, attributes);
    L.debug(FTAG, 'result:', result);
    return result;
  } catch (e) {
    L.warn(FTAG, options, e);
  }
};

export const xeClearCognitoTokens = (dispatch, getState) => {
  const FTAG = 'clearCognitoTokens';

  try {
    const user = getState()?.cognito?.user;
    if (!user) L.info(FTAG, 'No Cognito user:', user);

    if ('function' === typeof user?.signOut) return user?.signOut();

    L.warn(FTAG, 'No function at user.signOut:', user?.signOut);
  } catch (e) {
    L.warn(FTAG, e);
  }
};

export const xeLogoutUser = () => (dispatch, getState) => {
  const FTAG = 'logoutUser()';

  L.info('Logging out user...');

  L.debug(FTAG, 'xeClearCognitoTokens:', xeClearCognitoTokens(dispatch, getState));
  L.debug(FTAG, 'xeCognitoLogout:', dispatch(xeCognitoLogout()));
  L.debug(FTAG, 'xeLogoutSuccess:', dispatch(xeLogoutSuccess()));

  locationReplace('/salon/#/login');
};

export const withRcmPasswordReset = Component => {
  const name = Component ? Component.displayName || Component.name || 'UnnamedComponent' : 'UnknownComponent';

  const WithRcmPasswordReset = props => (
    <RcmPasswordReset>
      <Component {...props} />
    </RcmPasswordReset>
  );

  hoistNonReactStatics(WithRcmPasswordReset, Component);
  WithRcmPasswordReset.displayName = `withRcmPasswordReset(${name})`;

  return WithRcmPasswordReset;
};

export const withRcmLogin = Component => {
  const name = Component ? Component.displayName || Component.name || 'UnnamedComponent' : 'UnknownComponent';

  const WithRcmLogin = props => (
    <RcmLogin>
      <Component {...props} />
    </RcmLogin>
  );

  hoistNonReactStatics(WithRcmLogin, Component);
  WithRcmLogin.displayName = `withRcmLogin(${name})`;

  return WithRcmLogin;
};

export const withRcmNewPasswordRequired = Component => {
  const name = Component ? Component.displayName || Component.name || 'UnnamedComponent' : 'UnknownComponent';

  const WithRcmNewPasswordRequired = props => (
    <RcmNewPasswordRequired>
      <Component {...props} />
    </RcmNewPasswordRequired>
  );

  hoistNonReactStatics(WithRcmNewPasswordRequired, Component);
  WithRcmNewPasswordRequired.displayName = `withRcmNewPasswordRequired(${name})`;

  return WithRcmNewPasswordRequired;
};
